/**
 * Anchor Nav (tikvah/anchor-nav)
 *
 * Front-end behaviour:
 *  - Mobile menu toggle (aria-expanded on the button).
 *  - Scrollspy: highlight the current section's link as the user scrolls.
 *  - Smooth scroll to anchor on click with sticky-header offset.
 *
 * Renders alongside markup produced by templates/custom-blocks/anchor-nav.twig.
 * Each anchor-section exposes `data-anchor-id` matching the link's `href="#id"`.
 */

( function () {
    'use strict';

    var STICKY_OFFSET = 80;

    function init( root ) {
        // The mobile sticky stack (logo + toggle + progress + current-label)
        // lives at the region root, OUTSIDE this aside, so its
        // position:sticky can ride the full region height on mobile. Scope
        // those lookups to the region; everything else stays inside the
        // aside. The overlay menu drops below the entire stack so it
        // doesn't cover the bar.
        var region = root.closest( '.tikvah-anchor-nav-region' ) || document;
        var toggle = region.querySelector( '.tikvah-anchor-nav__mobile-toggle' );
        var mobileBar = region.querySelector( '.tikvah-anchor-nav__mobile-stack' )
            || region.querySelector( '.tikvah-anchor-nav__mobile-bar' );
        var menu   = root.querySelector( '.tikvah-anchor-nav__menu' );
        var progressFill = region.querySelector( '[data-anchor-progress]' );
        var links  = Array.prototype.slice.call(
            root.querySelectorAll( '.tikvah-anchor-nav__item a[href^="#"]' )
        );
        var currentLabel = region.querySelector( '[data-anchor-current]' );

        function positionMenuUnderBar() {
            if ( ! menu || ! mobileBar ) return;
            var barBottom = mobileBar.getBoundingClientRect().bottom;
            menu.style.top = barBottom + 'px';
            menu.style.maxHeight = ( window.innerHeight - barBottom ) + 'px';
        }

        function setMenuOpen( open ) {
            if ( ! toggle ) return;
            toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
            if ( ! menu ) return;
            menu.classList.toggle( 'is-open', open );

            if ( open ) {
                // The menu becomes position:fixed (CSS) which removes it
                // from the .col-lg-3 flow and reshapes the page. Defer the
                // bar measurement until layout has flushed AND the browser
                // has had a chance to clamp any newly-invalid scrollTop,
                // otherwise we'd pin the menu to a stale bar position.
                requestAnimationFrame( function () {
                    requestAnimationFrame( positionMenuUnderBar );
                } );
            } else {
                menu.style.top = '';
                menu.style.maxHeight = '';
            }
        }

        // --- Mobile toggle -------------------------------------------------
        if ( toggle && menu ) {
            toggle.addEventListener( 'click', function () {
                var expanded = toggle.getAttribute( 'aria-expanded' ) === 'true';
                setMenuOpen( ! expanded );
            } );

            // Keep the menu pinned to the bar if the viewport resizes or
            // scrolls while open (bar's sticky position can change with
            // scroll until it reaches top:0).
            var keepPinned = function () {
                if ( toggle.getAttribute( 'aria-expanded' ) === 'true' ) {
                    positionMenuUnderBar();
                }
            };
            window.addEventListener( 'resize', keepPinned );
            window.addEventListener( 'scroll', keepPinned, { passive: true } );
        }

        // --- Mobile pin (Program template) --------------------------------
        // Pin the logo+bar stack to the top of the viewport the moment the
        // user scrolls past the hero, instead of waiting for the stack's
        // natural position at the top of the anchor-nav region (which sits
        // below the tall stats band). Uses position:fixed (CSS) toggled via
        // `.is-pinned`, and reserves matching top padding on the region so
        // the first section clears the pinned bar with no layout shift.
        var stack = region.querySelector
            ? region.querySelector( '.tikvah-anchor-nav__mobile-stack' )
            : null;
        if ( stack && document.body.classList.contains( 'page-program' ) ) {
            // Trigger off the hero LOGO, not the whole hero, so the bar
            // appears as soon as the hero lockup scrolls out of view — the
            // pinned bar carries the same logo, so it reads as the lockup
            // "sticking" to the top. Falls back to the hero block, then 0.
            var trigger = document.querySelector( '.program-hero__logo' )
                || document.querySelector( '.program-hero' );
            var mqMobile = window.matchMedia( '(max-width: 991px)' );

            var updatePin = function () {
                if ( ! mqMobile.matches ) {
                    stack.classList.remove( 'is-pinned' );
                    region.style.paddingTop = '';
                    return;
                }
                var navH = stack.offsetHeight;
                // Reserve the stack's height + breathing room at the region
                // top so the first section isn't hidden under the fixed bar.
                region.style.paddingTop = ( navH + 24 ) + 'px';

                var y = window.pageYOffset;
                var triggerBottom = trigger
                    ? ( trigger.getBoundingClientRect().bottom + y )
                    : 0;
                var regionBottom = region.getBoundingClientRect().bottom + y;
                // Pin once the hero logo has scrolled out the top of the
                // viewport. The hero is the same dark blue as the bar, so the
                // bar reads as the lockup "sticking" to the top as it slides
                // in. Unpin near the region's end so the bar scrolls away
                // before the contact/footer rather than floating over them.
                var pinned = y > triggerBottom && ( regionBottom - y ) > navH;
                stack.classList.toggle( 'is-pinned', pinned );
            };

            updatePin();
            window.addEventListener( 'scroll', updatePin, { passive: true } );
            window.addEventListener( 'resize', updatePin );
        }

        // --- Smooth scroll + close mobile menu on click --------------------
        links.forEach( function ( link ) {
            link.addEventListener( 'click', function ( e ) {
                var id = link.getAttribute( 'href' ).slice( 1 );
                var target = id ? document.getElementById( id ) : null;
                if ( ! target ) {
                    return;
                }
                e.preventDefault();
                var y = target.getBoundingClientRect().top + window.pageYOffset - STICKY_OFFSET;
                window.scrollTo( { top: y, behavior: 'smooth' } );

                if ( toggle && toggle.getAttribute( 'aria-expanded' ) === 'true' ) {
                    setMenuOpen( false );
                }
            } );
        } );

        // --- Progress bar -------------------------------------------------
        // Tracks how far the user has scrolled through the anchor-nav
        // region. Fills from 0% (region just entering viewport) to 100%
        // (bottom of region reached the bottom of the viewport).
        if ( progressFill && region ) {
            var ticking = false;
            var updateProgress = function () {
                ticking = false;
                var rect = region.getBoundingClientRect();
                var viewport = window.innerHeight || document.documentElement.clientHeight;
                var scrollable = Math.max( rect.height - viewport, 1 );
                var scrolled = -rect.top;
                var progress = Math.max( 0, Math.min( 1, scrolled / scrollable ) );
                progressFill.style.width = ( progress * 100 ) + '%';
            };
            var requestUpdate = function () {
                if ( ticking ) return;
                ticking = true;
                window.requestAnimationFrame( updateProgress );
            };
            window.addEventListener( 'scroll', requestUpdate, { passive: true } );
            window.addEventListener( 'resize', requestUpdate );
            updateProgress();
        }

        // --- Scrollspy via IntersectionObserver ---------------------------
        if ( ! ( 'IntersectionObserver' in window ) || links.length === 0 ) {
            return;
        }

        var sections = links
            .map( function ( link ) {
                var id = link.getAttribute( 'href' ).slice( 1 );
                return id ? document.getElementById( id ) : null;
            } )
            .filter( Boolean );

        if ( sections.length === 0 ) {
            return;
        }

        var observer = new IntersectionObserver(
            function ( entries ) {
                entries.forEach( function ( entry ) {
                    if ( ! entry.isIntersecting ) {
                        return;
                    }
                    var id = entry.target.id;
                    links.forEach( function ( link ) {
                        var isCurrent = link.getAttribute( 'href' ) === '#' + id;
                        link.parentElement.classList.toggle( 'is-active', isCurrent );
                        if ( isCurrent ) {
                            link.setAttribute( 'aria-current', 'true' );
                            if ( currentLabel ) {
                                currentLabel.textContent = link.textContent.trim();
                            }
                        } else {
                            link.removeAttribute( 'aria-current' );
                        }
                    } );
                } );
            },
            {
                rootMargin: '-' + STICKY_OFFSET + 'px 0px -60% 0px',
                threshold: 0
            }
        );

        sections.forEach( function ( section ) {
            observer.observe( section );
        } );
    }

    function bootstrap() {
        var roots = document.querySelectorAll( '.tikvah-anchor-nav' );
        Array.prototype.forEach.call( roots, init );
    }

    if ( document.readyState === 'loading' ) {
        document.addEventListener( 'DOMContentLoaded', bootstrap );
    } else {
        bootstrap();
    }
} )();
